// ========================================
// App.tsx - Main Application Router
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 11, 2026
// 
// PURPOSE: Defines all application routes and navigation structure
// 
// ROUTE ORGANIZATION:
// - Public Routes (wrapped with Layout): Have consistent header/footer
// - Public Routes (NO Layout): Full-screen pages (Login, Register, VerifyEmail)
// - Protected User Routes: Require valid user JWT token
// - Admin Routes: Require valid admin JWT token
// - Catch-all: Redirects unknown paths to homepage
// 
// IMPORTANT FOR FUTURE MAINTAINERS:
// - The Layout component provides header and footer for public pages
// - Login/Register pages do NOT use Layout (they need full-screen forms)
// - Dashboard and Admin pages have their own layouts
// - Add new public pages to the Layout wrapper group
// ========================================

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// ========================================
// LAYOUT COMPONENT - Provides header/footer for public pages
// ========================================
import Layout from './components/Layout';
import SessionTimeout from './components/SessionTimeout';

// ========================================
// PAGE IMPORTS - Public Pages (use Layout)
// ========================================
import Landing from './pages/Landing';           // Marketing/landing page
import HowItWorks from './pages/HowItWorks';    // Detailed step-by-step guide
import AboutUs from './pages/AboutUs';          // Company information
import ContactUs from './pages/ContactUs';      // Contact form and information
import Compliance from './pages/Compliance';    // Legal & regulatory compliance
import Security from './pages/Security';        // Security overview
import FAQ from './pages/FAQ';                  // Frequently asked questions
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Features from './pages/Features';
import Pricing from './pages/Pricing';

// ========================================
// PAGE IMPORTS - Public Pages (NO Layout - full screen)
// ========================================
import Login from './pages/Login';               // User login
import Register from './pages/Register';         // User registration
import VerifyEmail from './pages/VerifyEmail';   // Email verification after registration
import VerifyPrompt from './pages/VerifyPrompt';

// ========================================
// PAGE IMPORTS - Protected User Pages
// ========================================
import Dashboard from './pages/Dashboard';               // Main user dashboard
import AddAsset from './pages/AddAsset';                 // Create new asset
import Beneficiaries from './pages/Beneficiaries';       // Manage external beneficiaries
import NextOfKin from './pages/NextOfKin';               // Manage Global Next of Kin
import Trustees from './pages/Trustees';                 // Manage trustees
import AcceptTrustee from './pages/AcceptTrustee';       // Trustee accepts invitation
import TrusteeVerification from './pages/TrusteeVerification'; // Trustee verifies user death
import AssetDetails from './pages/AssetDetails';         // View/edit single asset
import BeneficiaryClaim from './pages/BeneficiaryClaim'; // Beneficiary claims inheritance
import Upgrade from './pages/Upgrade';                   // Upgrade plan (one-time payment)
import Settings from './pages/Settings';                 // User account settings
import Account from './pages/Account';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import TrusteeActions from './pages/TrusteeActions';

// ========================================
// PAGE IMPORTS - Admin Pages
// ========================================
import AdminLogin from './pages/AdminLogin';             // Admin authentication
import AdminDashboard from './pages/AdminDashboard';     // Admin control panel
import IntegrityAlerts from './components/admin/IntegrityAlerts'; // Data integrity monitoring

// Test Mode Banner (shown on ALL pages during testing)
const TestModeBanner = () => {
  return (
    <div style={{
      background: '#FFD700',
      color: '#1A1A1A',
      textAlign: 'center',
      padding: '8px 16px',
      fontSize: '12px',
      fontWeight: 'bold',
      position: 'sticky',
      top: 0,
      zIndex: 9999,
      borderBottom: '2px solid #B8860B'
    }}>
      ⚠️ TESTING MODE — This site is for family and friends testing only. Not open to the public. ⚠️
    </div>
  );
};

function App() {
	const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);
  
  return (
    <Router>
	  <TestModeBanner />
      {isLoggedIn && <SessionTimeout timeoutMinutes={20} warningMinutes={1} />}
      <Routes>
        
        {/* ========================================
            PUBLIC ROUTES - WITH LAYOUT (Header + Footer)
            These pages share the same navigation bar and footer
            ======================================== */}
        
        {/* Use element={<Layout />} as a wrapper - the actual page goes inside as children */}
        {/* The <Outlet /> pattern: Layout renders its children inside the main content area */}
        <Route element={<Layout />}>
		
          <Route path="/" element={
            <PublicRoute redirectToDashboard={true}>
             <Landing />
            </PublicRoute>
           } />
       
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/security" element={<Security />} />
          <Route path="/faq" element={<FAQ />} />
		  <Route path="/privacy" element={<PrivacyPolicy />} />
		  <Route path="/terms" element={<TermsOfService />} />
		  <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
        </Route>
        
        {/* ========================================
            PUBLIC ROUTES - NO LAYOUT (Full Screen)
            These pages need full-screen forms without header/footer
            ======================================== */}
        
        {/* Authentication Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
		<Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        
        {/* Email Verification - User clicks link from email */}
        {/* Token parameter contains 32-character verification code */}
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
		<Route path="/verify-prompt" element={<VerifyPrompt />} />
        
        {/* ========================================
            PROTECTED USER ROUTES
            Note: Authentication is checked in each page component
            Unauthenticated users are redirected to /login
            These pages have their own dashboard layout (not using Layout)
            ======================================== */}
        
        {/* Main Dashboard - User's home after login */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
		<Route path="/trustee-actions" element={<ProtectedRoute><TrusteeActions /></ProtectedRoute>} />
        
        {/* Asset Management */}
        <Route path="/add-asset" element={<ProtectedRoute><AddAsset /></ProtectedRoute>} />
        <Route path="/asset/:id" element={<ProtectedRoute><AssetDetails /></ProtectedRoute>} />
        
        {/* Beneficiary Management */}
        <Route path="/beneficiaries" element={<ProtectedRoute><Beneficiaries /></ProtectedRoute>} />
        <Route path="/next-of-kin" element={<ProtectedRoute><NextOfKin /></ProtectedRoute>} />
        
        {/* Trustee Management */}
        <Route path="/trustees" element={<ProtectedRoute><Trustees /></ProtectedRoute>} />
        <Route path="/accept-trustee/:token" element={<AcceptTrustee />} />
        <Route path="/verify-user/:token" element={<TrusteeVerification />} />
        
        {/* Claims & Inheritance */}
        <Route path="/claim/:token" element={<BeneficiaryClaim />} />
        
        {/* Account Management */}
        <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
		<Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        
        {/* ========================================
            ADMIN ROUTES - Separate authentication system
            Uses adminToken (different from user token)
            ======================================== */}
        
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/integrity" element={<IntegrityAlerts />} />
        
        {/* ========================================
            CATCH-ALL ROUTE - 404 Handling
            IMPORTANT: Must be the LAST route
            Redirects any unknown path to homepage
            ======================================== */}
        <Route path="*" element={<Navigate to="/" replace />} />
        
      </Routes>
    </Router>
  );
}

export default App;